import { ROUTE, ROUTES } from "@/core/routes/constants";
import {
  IconlyBuildingOffice,
  IconlyDelivery3,
  IconlyDocumentPencil,
  IconlyDocumentTwoLines,
  IconlyFilter,
  IconlySearchCheck,
  IconlyTwoUsers,
  IconlyWallet,
} from "@/core/assets/icons";
import MessageMenuIcon from "@/assets/icons/message-menu.svg?react";
import SearchMenuIcon from "@/assets/icons/search-menu.svg?react";
import { useAuth } from "@/core/components/auth/store/auth";
import { useFeatureBadge } from "@/core/hooks/useFeatureBadge";
import { useOpenServiceManagement } from "@/my-offices/hooks/use-open-service-management";
import { ChartBar } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SidebarMenuGroup, SidebarMenuGroupItem } from "../../sidebar/types";

function isReportEnabledForLeague(league: number): boolean {
  try {
    const leagues = JSON.parse(
      import.meta.env.VITE_ALLOWED_REPORT_LEAGUES,
    ) as number[];
    return leagues.includes(league);
  } catch {
    return false;
  }
}

/**
 * Menu do shell WOS (PED-12262, Fase 1).
 * Mesmos itens, rotas, permissões e trackers do `useMenu` legado,
 * reagrupados nas seções do design: Equipe / Escritórios / Financeiro.
 */
export function useWosMenu() {
  const { userInfo, hasPermission } = useAuth();

  const [translate] = useTranslation(["core"]);

  const activeBadges = useFeatureBadge();
  const { openServiceManagement } = useOpenServiceManagement();

  const league = userInfo?.user?.leagues_id?.[0] || 0;
  const isReportEnabled = isReportEnabledForLeague(league);

  const items = useMemo(() => {
    const badge = (id: string) =>
      activeBadges.has(id)
        ? (translate("app-menu-badge") as string)
        : undefined;

    const filterItems = (data: SidebarMenuGroupItem[]) =>
      data.filter((item) => item.enabled);

    const result: SidebarMenuGroup[] = [
      {
        title: translate("app-menu-team"),
        enabled: true,
        items: filterItems([
          {
            id: "dashboard",
            title: translate("app-menu-dashboard"),
            link: ROUTES.DASHBOARD as ROUTE,
            icon: () => <IconlyFilter size={20} />,
            tracker: "MENU_DASHBOARD_CLICK",
            enabled: true,
          },
          {
            id: "members",
            title: translate("app-menu-members"),
            link: `${ROUTES.COMPANY.slice(0, -1)}${ROUTES.MEMBERS.slice(1)}` as ROUTE,
            icon: () => <IconlyTwoUsers size={20} />,
            tracker: "MENU_MEMBERS_CLICK",
            enabled: true,
          },
          {
            id: "insights",
            title: translate("app-menu-utilization"),
            link: ROUTES.INSIGHTS as ROUTE,
            icon: () => <IconlyDocumentTwoLines size={20} />,
            tracker: "MENU_UTILIZATION_CLICK",
            enabled: true,
          },
          {
            id: "reasons-of-use",
            title: translate("app-menu-reasons-of-use"),
            link: ROUTES.REASONS_OF_USE as ROUTE,
            tracker: "MENU_REASONS_OF_USE_CLICK",
            icon: () => <IconlySearchCheck size={20} />,
            enabled: true,
          },
        ]),
      },
      {
        title: translate("app-menu-offices"),
        enabled: true,
        items: filterItems([
          {
            id: "private-offices",
            title: translate("app-menu-private-offices"),
            link: `${ROUTES.MY_OFFICES.slice(0, -1)}` as ROUTE,
            icon: () => <IconlyBuildingOffice size={20} />,
            tracker: "MENU_PRIVATE_OFFICES_CLICK",
            enabled: hasPermission("read-private-rooms"),
          },
          {
            // PED-13066: abre a Gestão de Serviços (ex-Nexus) já autenticada,
            // numa nova aba. Sem `link` fixo — a URL só existe depois do
            // handoff de sessão (ver useOpenServiceManagement).
            id: "service-management",
            title: translate("app-menu-service-management"),
            icon: () => <MessageMenuIcon />,
            onSelect: openServiceManagement,
            enabled: hasPermission("read-private-rooms"),
          },
          {
            id: "explore-spaces",
            title: translate("app-menu-explore-spaces"),
            link: import.meta.env.VITE_LISTING_URL,
            icon: () => <SearchMenuIcon />,
            tracker: "CLICK_LINK_EXPLORE_SPACES",
            enabled: true,
            external: true,
          },
        ]),
      },
      {
        title: translate("app-menu-financial"),
        enabled: true,
        items: filterItems([
          {
            id: "wallet",
            title: translate("app-menu-wallet"),
            link: ROUTES.WALLET as ROUTE,
            icon: () => <IconlyWallet size={20} />,
            enabled: true,
          },
          {
            id: "contracts",
            title: translate("app-menu-contracts"),
            link: ROUTES.CONTRACTS as ROUTE,
            icon: () => <IconlyDocumentPencil size={20} />,
            tracker: "MENU_CONTRACTS_CLICK",
            enabled: hasPermission("read-contracts"),
          },
          {
            id: "packages",
            title: translate("app-menu-packages"),
            link: ROUTES.PACKAGES.slice(0, -1) as ROUTE,
            icon: () => <IconlyDelivery3 size={20} />,
            tracker: "MENU_PACKAGES_CLICK",
            enabled: true,
            badge: badge("packages"),
          },
        ]),
      },
    ];

    if (isReportEnabled) {
      result.push({
        title: translate("app-menu-reports"),
        enabled: true,
        items: filterItems([
          {
            id: "reports",
            title: translate("app-menu-ho-reports"),
            link: ROUTES.REPORTS as ROUTE,
            icon: () => <ChartBar />,
            tracker: "MENU_REPORTS_CLICK",
            enabled: true,
          },
        ]),
      });
    }

    return result
      .filter((group) => group.enabled)
      .filter((group) => group.items.length > 0);
  }, [
    isReportEnabled,
    activeBadges,
    hasPermission,
    translate,
    openServiceManagement,
  ]);

  return { items };
}
